<template>
  <div class="pro-camera-inventory-table">
    <div class="level mb-3">
      <div class="level-left">
        <button-group />
      </div>
      <div class="level-right">
        <button class="button is-primary" @click="openAddProCameraInventoryModal">
          <span class="icon"><i class="fas fa-plus"></i></span>
          <span>Add Inventory</span>
        </button>
      </div>
    </div>

    <div class="field is-grouped is-grouped-multiline">
      <div class="control">
        <label class="label" for="pro-camera-status-filter">Status</label>
        <div class="select"><select id="pro-camera-status-filter" :value="proCameraFilters.status" @change="filter({ status: $event.target.value })">
          <option value="">All statuses</option>
          <option v-for="status in statuses" :key="status" :value="status">{{ status }}</option>
        </select></div>
      </div>
      <div class="control">
        <label class="label" for="pro-camera-sort">Sort by</label>
        <div class="select"><select id="pro-camera-sort" :value="proCameraFilters.sortBy" @change="filter({ sortBy: $event.target.value })">
          <option value="id">Inventory ID</option>
          <option value="last_seen_at">Last seen</option>
          <option value="created_at">Created at</option>
          <option value="serial_number">Serial number</option>
          <option value="status">Status</option>
        </select></div>
      </div>
      <div class="control">
        <label class="label" for="pro-camera-sort-order">Order</label>
        <div class="select"><select id="pro-camera-sort-order" :value="proCameraFilters.sortOrder" @change="filter({ sortOrder: $event.target.value })">
          <option value="desc">{{ dateSort ? 'Newest first' : 'Descending' }}</option>
          <option value="asc">{{ dateSort ? 'Oldest first' : 'Ascending' }}</option>
        </select></div>
      </div>
      <div class="control pro-camera-filter-reset"><button class="button" @click="filter({ status: '', sortBy: 'id', sortOrder: 'desc' })">Reset filters</button></div>
    </div>
    <div v-if="proCameraListError" class="notification is-danger is-light" role="alert">
      {{ proCameraListError }} <button class="button is-small" @click="fetchList">Retry</button>
    </div>
    <div class="table-container">
      <table class="table is-fullwidth is-striped is-hoverable">
        <thead>
          <tr>
            <th>ID</th>
            <th>Serial Number</th>
            <th>Status</th>
            <th>Camera ID</th>
            <th>Created At</th>
            <th>Last seen</th>
            <th>Actions</th>
          </tr>
        </thead>

        <transition name="fade" mode="out-in">
          <tbody v-if="isLoadingSync" key="loading" class="skeleton-tbody">
            <tr v-for="n in skeletonRows" :key="`skeleton-${n}`" class="skeleton-row">
              <td><div class="skeleton skeleton-text"></div></td>
              <td><div class="skeleton skeleton-text"></div></td>
              <td><div class="skeleton skeleton-text"></div></td>
              <td><div class="skeleton skeleton-text"></div></td>
              <td><div class="skeleton skeleton-text"></div></td>
              <td><div class="skeleton skeleton-text"></div></td>
              <td><div class="skeleton skeleton-text"></div></td>
            </tr>
          </tbody>

          <tbody v-else-if="pro_camera_inventories.length > 0" key="data" class="data-tbody">
            <tr v-for="(item, i) in pro_camera_inventories" :key="i">
              <td>{{ item.id }}</td>
              <td>
                {{ item.serial_number }}
              </td>
              <td>
                <span :class="['tag', statusClass(item.camera_status)]">
                  {{ item.camera_status }}
                </span>
              </td>
              <td>{{ item.camera_id || '—' }}</td>
              <td>{{ formatDate(item.created_at) }}</td>
              <td>{{ lastSeen(item.last_seen_at) }}</td>
              <td>
                <div class="buttons are-small">
                  <button
                    class="button is-warning is-light"
                    @click="openProCameraInventoryModal(item)"
                  >
                    <span class="icon"><i class="fas fa-edit"></i></span>
                  </button>
                  <button
                    v-if="confirmDeleteId !== item.id"
                    class="button is-danger is-light"
                    @click="confirmDeleteId = item.id"
                  >
                    <span class="icon"><i class="fas fa-trash"></i></span>
                  </button>
                  <template v-else>
                    <button class="button is-danger" @click="confirmDelete(item)">
                      Confirm
                    </button>
                    <button class="button" @click="confirmDeleteId = null">
                      Cancel
                    </button>
                  </template>
                </div>
              </td>
            </tr>
          </tbody>

          <tbody v-else key="empty">
            <tr>
              <td colspan="7" class="has-text-centered empty-state">
                <div class="empty-state-content">
                  <p class="has-text-grey">
                    <span class="icon is-large"><i class="fas fa-camera-retro fa-2x"></i></span>
                  </p>
                  <p class="has-text-grey">No pro camera inventories found</p>
                </div>
              </td>
            </tr>
          </tbody>
        </transition>
      </table>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import ButtonGroup from "@/components/ButtonGroup.vue";
import { formatDate as formatTimestamp } from "@/utils/date_converter";

export default {
  name: "proCameraInventoryList",
  components: { "button-group": ButtonGroup },
  data() {
    return {
      skeletonRows: 10,
      confirmDeleteId: null,
      statuses: ["unclaimed", "stock", "assigned", "returning", "created", "pending", "claimed", "active", "disabled", "lost", "retired"],
    };
  },
  computed: {
    ...mapState(["pro_camera_inventories", "isLoadingSync", "proCameraFilters", "proCameraListError"]),
    dateSort() { return ["last_seen_at", "created_at"].includes(this.proCameraFilters.sortBy); },
  },
  methods: {
    ...mapActions([
      "fetchList",
      "openProCameraInventoryModal",
      "openAddProCameraInventoryModal",
      "deleteProCameraInventory",
    ]),
    statusClass(status) {
      if (status === "assigned") return "is-success";
      if (status === "stock") return "is-info";
      if (status === "returning") return "is-warning";
      if (status === "created") return "is-light";
      if (status === "active") return "is-success";
      if (status === "claimed") return "is-info";
      if (status === "pending") return "is-warning";
      if (status === "disabled") return "is-danger";
      return "is-light";
    },
    filter(value) {
      this.$store.commit("SET_PRO_CAMERA_FILTERS", value);
      this.$store.commit("SET_CURRENT_PAGE", { tabIndex: this.$store.getters.activeTabIndex, page: 1 });
      this.fetchList();
    },
    lastSeen(value) { return value ? formatTimestamp(value, { showTime: true, showRelative: false }) : "Never"; },
    formatDate(val) {
      if (!val) return "—";
      return new Date(val).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },
    confirmDelete(item) {
      this.confirmDeleteId = null;
      this.deleteProCameraInventory(item);
    },
  },
  mounted() {
    this.fetchList();
  },
};
</script>

<style scoped>
.pro-camera-filter-reset { align-self: flex-end; }
.table-container {
  position: relative;
  min-height: 400px;
}

.skeleton-row { pointer-events: none; }

.skeleton {
  background: linear-gradient(90deg, #f0f0f0 0%, #f8f8f8 50%, #f0f0f0 100%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
  display: inline-block;
}

.skeleton-text { height: 20px; width: 80%; max-width: 120px; }

@keyframes shimmer {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}

.fade-enter-active, .fade-leave-active { transition: opacity 0.3s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
.fade-enter-to, .fade-leave-from { opacity: 1; }

.empty-state { padding: 3rem !important; }
.empty-state-content { display: flex; flex-direction: column; align-items: center; gap: 0.5rem; }
.empty-state-content .icon { color: #dbdbdb; }

.entity-link { display: inline-flex; align-items: center; gap: 0.25rem; color: inherit; }
.edit-icon { opacity: 0; transition: opacity 0.15s; }
.entity-link:hover .edit-icon { opacity: 1; }
</style>
